/**
 * Zonenchat in the client (amendment 16): which district this device is in, that district's
 * messages, and how many of them are new.
 *
 * The district is worked out here, from the device's own position, and only its index ever
 * leaves the device: when it shares the district (presence, which the person switches on) and
 * when the chat is read or written. Without sharing, the chat is fetched only while its panel is
 * open — polling your district's chat in the background would tell the server where you are as
 * surely as sharing does, without anybody having said yes to it. So the badge and the snackbar
 * are for those who share, and everybody else sees what is new when they open the chat.
 *
 * Messages are sent directly, not through the outbox, for the reason given in `db/zones.ts`.
 *
 * Some messages carry a game (amendments 19 and 20). A game can change after its message
 * arrived — somebody takes the invitation, somebody moves, a vote closes — so every poll also
 * reads again the messages whose game is still open or running, and while this device plays one
 * against somebody, or waits for a vote it is in, the chat polls faster.
 */

import type { Duel, DuelView, Hand, MessageKind } from '$domain/games.ts';
import { DUEL_NAMES } from '$domain/games.ts';
import type { PartyGame, PartyView, Vote, Wager } from '$domain/party.ts';
import { zoneOf } from '$domain/zones.ts';
import { alerts } from './alerts.svelte.ts';
import { request } from './api.ts';

export type Author = { username: string; avatarEmoji: string };

/** A message's Miesmuschel or game for two, as the server lets this person see it. */
export type GameView = { kind: 'conch'; answer: string } | (DuelView & { guest: Author | null });

export type ZoneMessage = {
  id: string;
  kind: MessageKind;
  author: Author;
  body: string;
  createdAt: string;
  mine: boolean;
  game: GameView | null;
  /** A drinking game (amendment 20), kept out of `game` so that older apps show the text. */
  party: PartyView | null;
};

export type Move =
  | { square: number }
  | { hand: Hand }
  | { resign: true }
  | { vote: Vote }
  | { close: true }
  | { wager: Wager }
  | { text: string }
  | { confirm: string }
  | { remove: string }
  | { report: string };

/** What the snackbar says: a new message, or that somebody took this device's invitation. */
export type Snack = { icon: string; who: string; text: string };

export function isDuelView(game: GameView | null): game is DuelView & { guest: Author | null } {
  return game !== null && game.kind !== 'conch';
}

/**
 * A game that can still change: an invitation nobody took yet, a game being played, a vote, a
 * wager whose hour is not up, a bingo nobody has filled.
 */
function live(message: ZoneMessage): boolean {
  const game = message.party ?? message.game;
  if (game === null || game.kind === 'conch' || game.kind === 'dice') return false;
  return game.status === 'open' || game.status === 'running';
}

/**
 * A live game this device is in the middle of: its own invitation, a game it plays, a vote it
 * asked for or cast. A wager and a bingo take an hour or an evening, and a few seconds more
 * between two polls changes nothing about them.
 */
function playing(message: ZoneMessage): boolean {
  if (!live(message)) return false;
  const party = message.party;
  if (party !== null) return party.kind === 'hotcold' && (party.asked || party.mine !== null);
  return isDuelView(message.game) && message.game.you !== null;
}

const SHARE_KEY = 'hotspotter.zone.share';
const READ_KEY = 'hotspotter.zone.read';
/** How often to look for new messages: often with the chat open, rarely with it closed. */
const OPEN_POLL_MS = 5_000;
const CLOSED_POLL_MS = 30_000;
/** While this device plays: the other player's move should not take five seconds to show. */
const PLAY_POLL_MS = 2_000;
/** Games read again per poll, newest first — the server takes no more (`REFRESH_MAX`). */
const REFRESH_MAX = 20;
/**
 * A shared district is said again this often while the device stays in it, so that it does not
 * lapse — the server forgets it two hours after the last time — while the app is open there.
 */
const PRESENCE_REFRESH_MS = 30 * 60 * 1000;
/** A fix this old is still good enough to know the district: it is 2.5 km across. */
const FIX_MAX_AGE_MS = 5 * 60 * 1000;
/** How long the snackbar stays. */
const SNACK_MS = 6_000;

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function save(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private browsing or a full quota: the setting holds for this session and no longer.
  }
}

class ZoneChat {
  /** The district this device is in, as far as it knows. */
  here = $state<string | undefined>();
  /** The district whose chat is shown: `here`, unless a push opened another one. */
  shown = $state<string | undefined>();
  messages = $state<ZoneMessage[]>([]);
  /** Whether this device tells the server its district. Per device, because it is about this one. */
  sharing = $state<boolean>(load(SHARE_KEY, false));
  open = $state(false);
  /** Why there is no district, in words for the panel. */
  problem = $state('');
  /**
   * The newest message from somebody else that arrived while the chat was closed — or that
   * somebody took this device's invitation to a game.
   */
  snack = $state<Snack | undefined>();
  /** The last message id read, per district. */
  read = $state<Record<string, string>>(load(READ_KEY, {}));

  /** New messages from others in the shown district: the badge on 💬. */
  readonly unread = $derived.by(() => {
    const zone = this.shown;
    if (zone === undefined) return 0;
    const last = Number(this.read[zone] ?? 0);
    return this.messages.filter((entry) => !entry.mine && Number(entry.id) > last).length;
  });

  #running = false;
  #timer: ReturnType<typeof setTimeout> | undefined;
  #snackTimer: ReturnType<typeof setTimeout> | undefined;
  #pinned = false;
  #loadedZone: string | undefined;
  #sharedZone: string | undefined;
  #sharedAt = 0;
  #fixAt = 0;

  start(): void {
    if (this.#running) return;
    this.#running = true;
    document.addEventListener('visibilitychange', this.#onVisibility);
    void this.tick();
  }

  stop(): void {
    this.#running = false;
    if (this.#timer !== undefined) clearTimeout(this.#timer);
    this.#timer = undefined;
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #onVisibility = () => {
    if (document.visibilityState === 'visible') void this.tick();
    else if (this.#timer !== undefined) clearTimeout(this.#timer);
  };

  /** Show the chat — of `zone`, when a push about another district asked for it. */
  show(zone?: string): void {
    if (zone !== undefined) {
      this.shown = zone;
      this.#pinned = true;
    }
    this.open = true;
    this.snack = undefined;
    void this.tick();
  }

  hide(): void {
    this.open = false;
    if (this.#pinned) {
      this.#pinned = false;
      this.shown = this.here;
    }
    this.#schedule();
  }

  /** Everything new in the shown district has been seen. */
  markRead(): void {
    const zone = this.shown;
    const last = this.messages.at(-1);
    if (zone === undefined || last === undefined) return;
    this.read = { ...this.read, [zone]: last.id };
    save(READ_KEY, $state.snapshot(this.read));
  }

  async setSharing(on: boolean): Promise<void> {
    this.sharing = on;
    save(SHARE_KEY, on);
    if (on) {
      this.#sharedZone = undefined;
      await this.tick();
      return;
    }
    this.#sharedZone = undefined;
    // Out of the district at once, not two hours from now.
    await request('/api/me/zone', { method: 'DELETE' }).catch(() => undefined);
  }

  async send(text: string): Promise<void> {
    await this.#post({ body: text });
  }

  /** A question to the magische Miesmuschel. The answer is drawn on the server. */
  async ask(question: string): Promise<void> {
    await this.#post({ kind: 'conch', body: question });
  }

  /**
   * An invitation to a game for two, which whoever takes it first plays — or a round of a
   * drinking game for everybody, or a roll of the Wabenwürfel (amendment 20).
   */
  async invite(kind: Duel | PartyGame): Promise<void> {
    await this.#post({ kind });
  }

  async join(id: string): Promise<void> {
    await this.#play(id, 'join', {});
  }

  async move(id: string, move: Move): Promise<void> {
    await this.#play(id, 'move', move);
  }

  async remove(id: string): Promise<void> {
    await request(`/api/zone-messages/${id}`, { method: 'DELETE' });
    this.messages = this.messages.filter((entry) => entry.id !== id);
  }

  async #post(payload: Record<string, unknown>): Promise<void> {
    const zone = this.shown;
    if (zone === undefined) throw new Error('Kein Viertel bekannt.');
    await request(`/api/zones/${zone}/messages`, {
      method: 'POST',
      body: JSON.stringify({ ...payload, opId: crypto.randomUUID() }),
    });
    await this.#fetch();
    this.markRead();
    // An invitation of this device's own is waited on at the quicker pace.
    this.#schedule();
  }

  /**
   * Take an invitation or move, and draw the answer at once. A refusal — too late, not your
   * turn — reads the chat again before it is thrown, so the board shows why.
   */
  async #play(id: string, verb: 'join' | 'move', payload: Record<string, unknown>) {
    try {
      const { message } = await request<{ message: ZoneMessage | null }>(
        `/api/zone-messages/${id}/${verb}`,
        { method: 'POST', body: JSON.stringify({ ...payload, opId: crypto.randomUUID() }) },
      );
      if (message !== null) {
        this.messages = this.messages.map((entry) => (entry.id === message.id ? message : entry));
      }
      this.#schedule();
    } catch (cause) {
      void this.tick();
      throw cause;
    }
  }

  async report(id: string): Promise<void> {
    await request(`/api/zone-messages/${id}/report`, { method: 'POST', body: '{}' });
  }

  /** Look for the district, say it if shared, and fetch what is new — then do it again later. */
  async tick(): Promise<void> {
    if (!this.#running) return;
    if (this.#timer !== undefined) clearTimeout(this.#timer);
    this.#timer = undefined;
    try {
      await this.#locate();
      await this.#share();
      await this.#fetch();
    } catch {
      // Offline, or the server said no to one request: the next tick tries again, and the
      // panel keeps what it had.
    }
    this.#schedule();
  }

  #schedule(): void {
    if (!this.#running || document.visibilityState !== 'visible') return;
    if (this.#timer !== undefined) clearTimeout(this.#timer);
    const pace = !this.open
      ? CLOSED_POLL_MS
      : this.messages.some(playing)
        ? PLAY_POLL_MS
        : OPEN_POLL_MS;
    this.#timer = setTimeout(() => void this.tick(), pace);
  }

  async #locate(): Promise<void> {
    // The geofence's watch is already running for anybody with alerts on: no second fix.
    const watched = alerts.position;
    if (watched !== undefined) {
      this.#setHere(zoneOf(watched.lat, watched.lon));
      return;
    }
    if (Date.now() - this.#fixAt < FIX_MAX_AGE_MS && this.here !== undefined) return;
    // Nobody asked for the district yet: no point waking the GPS for a closed chat.
    if (!this.open && !this.sharing) return;
    if (typeof navigator === 'undefined' || navigator.geolocation === undefined) {
      this.problem = 'Dieses Gerät kann seinen Standort nicht bestimmen.';
      return;
    }
    const fix = await new Promise<GeolocationPosition | undefined>((resolve) =>
      navigator.geolocation.getCurrentPosition(resolve, (cause) => {
        this.problem =
          cause.code === cause.PERMISSION_DENIED
            ? 'Ohne Standortfreigabe weiß die App nicht, in welchem Viertel du bist.'
            : 'Dein Standort lässt sich gerade nicht bestimmen.';
        resolve(undefined);
      }, { enableHighAccuracy: false, maximumAge: FIX_MAX_AGE_MS, timeout: 20_000 }),
    );
    if (fix === undefined) return;
    this.#fixAt = Date.now();
    this.#setHere(zoneOf(fix.coords.latitude, fix.coords.longitude));
  }

  #setHere(zone: string): void {
    this.problem = '';
    this.here = zone;
    if (!this.#pinned) this.shown = zone;
  }

  async #share(): Promise<void> {
    const zone = this.here;
    if (!this.sharing || zone === undefined) return;
    const fresh = zone === this.#sharedZone && Date.now() - this.#sharedAt < PRESENCE_REFRESH_MS;
    if (fresh) return;
    await request('/api/me/zone', { method: 'PUT', body: JSON.stringify({ zone }) });
    this.#sharedZone = zone;
    this.#sharedAt = Date.now();
  }

  async #fetch(): Promise<void> {
    const zone = this.shown;
    // Without sharing the chat is read only while it is open (see the header).
    if (zone === undefined || (!this.open && !this.sharing)) return;

    const continuing = zone === this.#loadedZone;
    const last = continuing ? this.messages.at(-1)?.id : undefined;
    const refresh = continuing ? this.messages.filter(live).slice(-REFRESH_MAX) : [];
    const query = new URLSearchParams();
    if (last !== undefined) query.set('after', last);
    if (refresh.length > 0) query.set('refresh', refresh.map((entry) => entry.id).join(','));
    const search = query.toString();
    const path = `/api/zones/${zone}/messages${search === '' ? '' : `?${search}`}`;
    const { messages, refreshed } = await request<{
      messages: ZoneMessage[];
      refreshed?: ZoneMessage[];
    }>(path);
    if (zone !== this.shown) return;

    if (!continuing) {
      this.messages = messages;
      this.#loadedZone = zone;
      return;
    }
    if (refreshed !== undefined) this.#refresh(refresh, refreshed);
    if (messages.length === 0) return;
    this.messages = [...this.messages, ...messages];
    const fromOthers = messages.filter((entry) => !entry.mine);
    const newest = fromOthers[fromOthers.length - 1];
    if (this.open) {
      this.markRead();
    } else if (newest !== undefined) {
      const said = newest.kind === 'conch' ? `🐚 ${newest.body}` : newest.body;
      this.#showSnack({ icon: '💬', who: newest.author.username, text: said });
    }
  }

  /**
   * Put the games read again in place of the ones shown. One the server did not send back has
   * been deleted or hidden, and goes; an invitation of this device's that somebody took, while
   * the chat was closed, is worth a snackbar.
   */
  #refresh(asked: ZoneMessage[], fresh: ZoneMessage[]): void {
    const byId = new Map(fresh.map((entry) => [entry.id, entry]));
    const gone = new Set(asked.filter((entry) => !byId.has(entry.id)).map((entry) => entry.id));
    this.messages = this.messages
      .filter((entry) => !gone.has(entry.id))
      .map((entry) => byId.get(entry.id) ?? entry);

    if (this.open) return;
    for (const before of asked) {
      const was = before.game;
      const now = byId.get(before.id)?.game ?? null;
      if (!before.mine || !isDuelView(was) || was.status !== 'open') continue;
      if (!isDuelView(now) || now.status !== 'running' || now.guest === null) continue;
      this.#showSnack({
        icon: '🎮',
        who: now.guest.username,
        text: `spielt ${DUEL_NAMES[now.kind]} mit dir`,
      });
    }
  }

  #showSnack(snack: Snack): void {
    this.snack = snack;
    if (this.#snackTimer !== undefined) clearTimeout(this.#snackTimer);
    this.#snackTimer = setTimeout(() => {
      this.snack = undefined;
    }, SNACK_MS);
  }
}

export const zone = new ZoneChat();
